import copyToClipboard from 'copy-to-clipboard';
import Image from 'next/image';
import Link from 'next/link';
import { useSnackbar } from 'notistack';
import { ReactNode } from 'react';

import ImageWithFallback from '@/components/ImageWithFallback';
import BlurredBackground from '@/components/Module/BlurredBackground';
import IconButton from '@/components/Module/IconButton';
import copy from '@/components/Pages/VenueDetail/assets/copy.svg';
import useViewport from '@/hooks/useViewport';
import { splitAddress } from '@/utils';

export type DetailCardProps = {
  type: string;
  name: string;
  address?: string | null;
  children?: ReactNode;
  imageUrl: string;
  websiteUrl?: string | null;
  instagramUrl?: string | null;
  facebookUrl?: string | null;
  spotifyUrl?: string | null;
};

const MobileDetailCard = (props: DetailCardProps) => {
  const {
    type,
    name,
    address,
    children,
    imageUrl,
    websiteUrl,
    instagramUrl,
    facebookUrl,
    spotifyUrl,
  } = props;
  const { enqueueSnackbar } = useSnackbar();

  return (
    <div className={`relative`}>
      <BlurredBackground imageUrl={imageUrl} height={375}>
        {spotifyUrl && (
          <div className={`absolute bottom-[69px] right-[24px]`}>
            <Link href={spotifyUrl}>
              <a className={`contents`}>
                <IconButton type={`spotify`} floating={true} />
              </a>
            </Link>
          </div>
        )}
      </BlurredBackground>
      <div className={`relative mt-[-75px] px-[24px]`}>
        <p className={`text-h5 font-normal text-gray-100`}>{type}</p>
        <p className={`break-words pt-[12px] text-h1 font-bold text-gray-600`}>
          {name}
        </p>
        {address && (
          <div
            className={`flex items-end gap-[2px] pt-[16px] text-b3 font-normal text-green-200`}
          >
            <div className={``}>
              {splitAddress(address).map((text, idx) => (
                <p key={idx}>{text}</p>
              ))}
            </div>
            <button
              className={`h-full flex-col justify-end p-[4px]`}
              onClick={() => {
                copyToClipboard(address, {
                  debug: true,
                  message: `Press #{key} to copy`,
                });
                enqueueSnackbar(`Address copied`);
              }}
            >
              <div className={`h-[16px] w-[16px]`}>
                <Image width={16} height={16} src={copy} alt="copy" />
              </div>
            </button>
          </div>
        )}
        {(websiteUrl || facebookUrl || instagramUrl) && (
          <div className={`mt-[38px]`}></div>
        )}
        <div className={`flex gap-[16px]`}>
          {websiteUrl && (
            <Link href={websiteUrl}>
              <a className="contents">
                <IconButton type={`globe`}></IconButton>
              </a>
            </Link>
          )}
          {facebookUrl && (
            <Link href={facebookUrl}>
              <a className="contents">
                <IconButton type={`facebook`}></IconButton>
              </a>
            </Link>
          )}
          {instagramUrl && (
            <Link href={instagramUrl}>
              <a className="contents">
                <IconButton type={`instagram`}></IconButton>
              </a>
            </Link>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

const DesktopDetailCard = (props: DetailCardProps) => {
  const {
    type,
    name,
    address,
    children,
    imageUrl,
    websiteUrl,
    instagramUrl,
    facebookUrl,
    spotifyUrl,
  } = props;
  const { enqueueSnackbar } = useSnackbar();

  return (
    <div className={`relative`}>
      <BlurredBackground imageUrl={imageUrl} height={456} />
      <div className={`relative mx-auto mt-[-242px] w-[928px] px-[24px]`}>
        <div className={`flex w-full justify-between gap-[49px]`}>
          <div className={`flex w-[550px] shrink flex-col justify-center`}>
            <p className={`text-h4 font-medium text-gray-100`}>{type}</p>
            <p
              className={`mt-[18px] break-words text-h0 font-bold text-gray-600`}
            >
              {name}
            </p>
            {address && (
              <div className={`mt-[18px] flex items-end gap-[2px]`}>
                <div className={`pt-[16px] text-b2 font-normal text-green-200`}>
                  {splitAddress(address).map((text, idx) => (
                    <p key={idx}>{text}</p>
                  ))}
                </div>
                <div>
                  <button
                    className={`h-full flex-col justify-end p-[4px]`}
                    onClick={() => {
                      copyToClipboard(address, {
                        debug: true,
                        message: `Press #{key} to copy`,
                      });
                      enqueueSnackbar(`Address copied`);
                    }}
                  >
                    <div className={`h-[16px] w-[16px]`}>
                      <Image width={16} height={16} src={copy} alt="copy" />
                    </div>
                  </button>
                </div>
              </div>
            )}
            {!address && <div className={`my-[30px]`}></div>}
            {(websiteUrl || facebookUrl || instagramUrl) && (
              <div className={`mt-[34px]`}></div>
            )}
            <div className={`flex gap-[16px]`}>
              {websiteUrl && (
                <Link href={websiteUrl}>
                  <a className="contents">
                    <IconButton type={`globe`}></IconButton>
                  </a>
                </Link>
              )}
              {facebookUrl && (
                <Link href={facebookUrl}>
                  <a className="contents">
                    <IconButton type={`facebook`}></IconButton>
                  </a>
                </Link>
              )}
              {instagramUrl && (
                <Link href={instagramUrl}>
                  <a className="contents">
                    <IconButton type={`instagram`}></IconButton>
                  </a>
                </Link>
              )}
            </div>
          </div>
          <div className={`relative h-[280px] shrink-0`}>
            <ImageWithFallback
              width={280}
              height={280}
              src={imageUrl}
              key={imageUrl}
              placeholder="blur"
              blurDataURL={imageUrl}
              alt="venue-image"
              className={`rounded-[29px] object-cover`}
            />
            {spotifyUrl && (
              <div className={`absolute bottom-[24px] right-[24px] z-10`}>
                <Link href={spotifyUrl}>
                  <a className={`contents`}>
                    <IconButton type={`spotify`} floating={true} />
                  </a>
                </Link>
              </div>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

const DetailCard = (props: DetailCardProps) => {
  const {} = props;
  const viewport = useViewport();

  if (viewport == `mobile` || viewport == `tablet`) {
    return <MobileDetailCard {...props} />;
  }
  if (viewport == `desktop`) {
    return <DesktopDetailCard {...props} />;
  }

  return null;
};

export default DetailCard;
